// SEO.js — per-route title, meta description, canonical, og/twitter, and
// optional JSON-LD structured data. Renders through react-helmet-async so
// the tags end up in <head> at runtime (and at prerender time if we ever
// wire a prerenderer). Every route should render <SEO /> near the top.

import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://premierleaguescorigami.com";
const SITE_NAME = "Premier League Scorigami";
const HOME_TITLE = `${SITE_NAME} | Every scoreline since 1992`;
const SUFFIX = ` | ${SITE_NAME}`;
const MAX_TITLE = 65; // about where Google cuts titles off
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`; // made by scripts/brand-assets.js

// Titles lead with what people search for. The site name is appended only
// when it fits and isn't already there; Google shows it anyway (from the
// WebSite JSON-LD).
export const pageTitle = (title) =>
  !title ? HOME_TITLE : /scorigami/i.test(title) || title.length + SUFFIX.length > MAX_TITLE ? title : `${title}${SUFFIX}`;

export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd,
}) {
  const fullTitle = pageTitle(title);
  const canonical = `${SITE_URL}${path}`;
  if (process.env.NODE_ENV === "development" && !noindex && description && (description.length > 160 || description.length < 70)) {
    console.warn(`SEO: ${path} description is ${description.length} characters; Google shows about 70–160.`, description);
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@pl_gami" />
      <meta name="twitter:creator" content="@pl_gami" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
